import React from "react";
import styled from "styled-components";
import { currencySymbol } from "../../formatting/currency-utils";
import formatCurrency from "../../formatting/formatCurrency";
import formatPercentage from "../../formatting/formatPercentage";

import { sqmToSiteAreaUnitRaw } from "../../formatting/formatArea";
import ChartUpRightIcon from "../../images/icons/chartUpRight.svg";
import CurrencyIcon from "../../images/icons/currencyGbp.svg";
import OverlappingCirclesIcon from "../../images/icons/overlappingCircles.svg";
import SwappingCirclesIcon from "../../images/icons/swappingCircles.svg";

export const MetricsRow = styled.div`
  display: flex;
  width: 100%;
`;

interface MetricProps {
  iconSrc: string;
  label: string;
  value: string | number;
}

export const Metric: React.FC<MetricProps> = ({ iconSrc, label, value }) => {
  return (
    <MetricWrapper>
      <MetricIcon src={iconSrc} />
      <MetricLabel>{label}</MetricLabel>
      <MetricValue>{value}</MetricValue>
    </MetricWrapper>
  );
};

const MetricWrapper = styled.div`
  border: 1px solid #dfdfdf;
  border-radius: 8px;
  padding: 16px;
  flex-basis: 100%;
  margin: 8px;
`;

const MetricIcon = styled.img`
  width: 25px;
  height: 25px;
  background: ${({ theme }) => theme.colors.blueFaint};
`;

const MetricLabel = styled.p`
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
`;

const MetricValue = styled.p`
  font-weight: 600;
  font-size: 24px;
  line-height: 26px;
`;

export const LeaseMetrics: React.FC<{
  totalBuildingAreaM2: number;
  annualRevenue: number;
  currencyCode: string;
  areaUnit: string;
}> = ({ totalBuildingAreaM2, annualRevenue, currencyCode, areaUnit }) => {
  return (
    <>
      <Metric
        iconSrc={ChartUpRightIcon}
        label={`Airspace rent (per ${areaUnit}²)`}
        value={`${currencySymbol(currencyCode)}
                        ${(
                          annualRevenue /
                          sqmToSiteAreaUnitRaw(totalBuildingAreaM2, areaUnit)
                        ).toFixed(2)}`}
      />
      <MetricsRow>
        <Metric
          iconSrc={OverlappingCirclesIcon}
          label="Up front investment"
          value={formatCurrency(0, currencyCode)}
        />
        <Metric
          iconSrc={CurrencyIcon}
          label="Revenue in year 1"
          value={formatCurrency(annualRevenue, currencyCode)}
        />
      </MetricsRow>
    </>
  );
};

interface LicenseMetricsProps {
  irr?: number;
  paybackMonths?: number;
  initialInvestment: number;
  annualRevenue: number;
  currencyCode: string;
}

export const LicenseMetrics: React.FC<LicenseMetricsProps> = ({
  irr,
  paybackMonths,
  initialInvestment,
  annualRevenue,
  currencyCode,
}) => {
  return (
    <>
      <MetricsRow>
        <Metric
          iconSrc={SwappingCirclesIcon}
          label="IRR"
          value={formatPercentage(irr || 0)}
        />
        <Metric
          iconSrc={ChartUpRightIcon}
          label="Payback (months)"
          value={paybackMonths || "N/A"}
        />
      </MetricsRow>
      <MetricsRow>
        <Metric
          iconSrc={OverlappingCirclesIcon}
          label="Up front investment"
          value={formatCurrency(initialInvestment, currencyCode)}
        />
        <Metric
          iconSrc={CurrencyIcon}
          label="Revenue in year 1"
          value={formatCurrency(annualRevenue, currencyCode)}
        />
      </MetricsRow>
    </>
  );
};
